import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import { UserGroupsColumn, UserGroupsFilters } from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { Codecs, PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilter, encodeOrFilter } from "./common";
import { createEncodeColumnFn } from "./controls/common";

// Keep it in sync with the definition in impero_shareable_links/src/api/page_state/user_groups.rs
const USER_GROUPS_ALL_COLUMNS: UserGroupsColumn[] = [
    "name",
    "members",
    "createdBy",
];
const encodeUserGroupsColumn: (col: string) => UserGroupsColumn =
    createEncodeColumnFn(USER_GROUPS_ALL_COLUMNS);

// Filters

function encodeUserGroupsPageFilters(
    filters: Array<SimpleOrFilter>
): UserGroupsFilters {
    return {
        name: encodeOrFilter("name", filters),
        members: encodeOrFilter("members", filters),
        createdBy: encodeOrFilter("createdBy", filters),
    };
}

function decodeUserGroupsPageFilters(
    filters: UserGroupsFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (filters.name) {
        output.push(decodeOrFilter("name", filters.name));
    }
    if (filters.members) {
        output.push(decodeOrFilter("members", filters.members));
    }
    if (filters.createdBy) {
        output.push(decodeOrFilter("createdBy", filters.createdBy));
    }
    return output;
}

// Page state

type UserGroupsPageStateWithType = PageStateData<"UserGroups"> & {
    type: "UserGroups";
};

function decodeUserGroupsPageState(
    pageState: UserGroupsPageStateWithType
): PageFilterAndColumns {
    const userGroupsPageState = unwrapPageStateAs(pageState, "UserGroups");
    const filter: SimpleAndFilter = {
        filters: decodeUserGroupsPageFilters(userGroupsPageState.filters),
    };
    const visibleColumnIds = new Set(userGroupsPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeUserGroupsPageState(
    filterAndColumns: PageFilterAndColumns
): UserGroupsPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    let filters: Array<SimpleOrFilter> = [];
    if (filter) {
        filters = filter.filters;
    }

    return {
        type: "UserGroups",
        filters: encodeUserGroupsPageFilters(filters),
        columns: Array.from(visibleColumnIds).map(encodeUserGroupsColumn),
        version: filterAndColumns.version,
    };
}

const pageStateUserGroupsCodecs: Codecs<
    PageFilterAndColumns,
    UserGroupsPageStateWithType
> = {
    encode: encodeUserGroupsPageState,
    decode: decodeUserGroupsPageState,
};

export {
    encodeUserGroupsPageState,
    decodeUserGroupsPageState,
    pageStateUserGroupsCodecs,
    USER_GROUPS_ALL_COLUMNS,
};
